import { ref } from 'vue'

import { createGroup } from '@/api/groups'
import type { LeafletProcessingStatusApiModel } from '@/api/leaflets'
import {
  fetchLeafletsProcessingStatus,
  uploadLeafletCover,
  uploadLeafletFilesAsync
} from '@/api/repositories/leafletsRepository'
import { addLeafletToStore } from '@/api/repositories/shopsRepository'
import { scheduleEventsBatch } from '@/api/scheduledEvents'
import { uuidRegExp } from '@/application.constants'
import i18n from '@/lang'
import type { LeafletForm } from '@/views/dashboard/types/LeafletForm'

import type { FileItem, LeafletTabData, ScheduledLongLink, ScheduledThumbnailLink } from '../domain/leaflet-form.types'
import type {
  ScheduledEventPayload,
  ScheduledLongLinkEventPayload,
  ScheduledThumbnailLinkEventPayload
} from './useScheduledEvents'

interface CreatedGroup {
  name: string
  id: string
}

interface GroupCreationResult {
  groupId: string | null
  newGroup: CreatedGroup | null
}

export interface CreatedLeafletResult {
  id: string
  naturalId: string
  fileList: FileItem[]
  firstPageFile: FileItem[]
  watermark: boolean
  requestFailed?: boolean
  message?: string
}

export interface FailedLeafletResult {
  requestFailed: true
  naturalId: string
  message: string
}

export type LeafletCreationResult = CreatedLeafletResult | FailedLeafletResult

export type LeafletProcessingStage = 'idle' | 'uploading' | 'generating'

interface UseLeafletCreateFlowOptions {
  processingPollIntervalMs?: number
  processingTimeoutMs?: number
}

interface UploadAttempt {
  leaflet: {
    id?: string
    naturalId: string
    fileList: FileItem[]
    firstPageFile?: FileItem[]
    watermark: boolean
    requestFailed?: boolean
  }
  initialStatus?: LeafletProcessingStatusApiModel
  error?: string
}

const DEFAULT_PROCESSING_POLL_INTERVAL_MS = 1_500
const DEFAULT_PROCESSING_TIMEOUT_MS = 15 * 60 * 1_000

function checkKnownMessages(errorData: unknown): string {
  if (typeof errorData === 'string') {
    return errorData
  }
  if (errorData && typeof errorData === 'object' && 'message' in errorData) {
    return String((errorData as { message: unknown }).message)
  }
  return 'Unknown error'
}

function creationErrorMessage(error: unknown): string {
  return checkKnownMessages((error as { response?: { data?: unknown } })?.response?.data)
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function useLeafletCreateFlow(options: UseLeafletCreateFlowOptions = {}) {
  const processingPollIntervalMs = options.processingPollIntervalMs ?? DEFAULT_PROCESSING_POLL_INTERVAL_MS
  const processingTimeoutMs = options.processingTimeoutMs ?? DEFAULT_PROCESSING_TIMEOUT_MS
  const processingStage = ref<LeafletProcessingStage>('idle')

  function prepareLeafletPayload(
    tab: LeafletTabData,
    availablePlatforms: string[]
  ): Omit<LeafletTabData, 'fileList' | 'firstPageFile' | 'watermark'> & { validTo?: string | null } {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fileList: _fileList, firstPageFile: _firstPageFile, watermark: _watermark, ...payload } = { ...tab }

    if (payload.untilEndOfSupply) {
      delete (payload as { validTo?: string | null }).validTo
    }

    availablePlatforms.forEach(platform => {
      const priorityKey = `${platform.toLowerCase()}Priority` as keyof typeof payload
      if (priorityKey in payload) {
        const value = payload[priorityKey]
        if (value === 0 || value === null || value === undefined) {
          ;(payload as Record<string, unknown>)[priorityKey] = null
        }
      }
    })

    payload.imagesURLPattern = payload.imagesURLPattern || null
    payload.numberOfImages = payload.imagesURLPattern && payload.numberOfImages ? payload.numberOfImages : null

    return payload
  }

  async function handleGroupCreation(
    groupId: string | null,
    alreadyCreatedGroups: CreatedGroup[]
  ): Promise<GroupCreationResult> {
    if (!groupId) {
      return { groupId: null, newGroup: null }
    }

    if (uuidRegExp.test(groupId)) {
      return { groupId, newGroup: null }
    }

    const existingGroup = alreadyCreatedGroups.find(group => group.name === groupId)
    if (existingGroup) {
      return { groupId: existingGroup.id, newGroup: null }
    }

    try {
      const response = await createGroup({ name: groupId })
      if (response.status === 201 && response.data) {
        const newGroup = { name: groupId, id: response.data.id }
        return { groupId: response.data.id, newGroup }
      }
      return { groupId: null, newGroup: null }
    } catch {
      return { groupId: null, newGroup: null }
    }
  }

  async function createLeaflets(
    tabs: LeafletTabData[],
    storeId: string,
    availablePlatforms: string[]
  ): Promise<LeafletCreationResult[]> {
    const alreadyCreatedGroups: CreatedGroup[] = []
    const preparedLeaflets: Array<{
      payload: ReturnType<typeof prepareLeafletPayload>
      tab: LeafletTabData
    }> = []

    for (const tab of tabs) {
      const { groupId, newGroup } = await handleGroupCreation(tab.groupId, alreadyCreatedGroups)

      if (newGroup) {
        alreadyCreatedGroups.push(newGroup)
      }

      const payload = prepareLeafletPayload(tab, availablePlatforms)
      ;(payload as { groupId: string | null }).groupId = groupId

      preparedLeaflets.push({ payload, tab })
    }

    const results: LeafletCreationResult[] = []

    for (let i = 0; i < preparedLeaflets.length; i++) {
      const { payload, tab } = preparedLeaflets[i]

      if (i > 0) {
        await delay(200)
      }

      try {
        const data = await addLeafletToStore(payload as unknown as LeafletForm, storeId, tab.watermark)

        results.push({
          id: data.id,
          naturalId: tab.naturalId,
          fileList: tab.fileList,
          firstPageFile: tab.firstPageFile,
          watermark: tab.watermark,
          requestFailed: false
        })
      } catch (err) {
        results.push({
          requestFailed: true,
          naturalId: tab.naturalId,
          message: creationErrorMessage(err)
        })
      }
    }

    return results
  }

  async function uploadFilesForLeaflets(
    createdLeaflets: Array<{
      id?: string
      naturalId: string
      fileList: FileItem[]
      firstPageFile?: FileItem[]
      watermark: boolean
      requestFailed?: boolean
    }>
  ): Promise<Array<{ id?: string; naturalId: string; requestFailed: boolean; message?: string }>> {
    const leafletsWithFiles = createdLeaflets.filter(
      leaflet => !leaflet.requestFailed && leaflet.fileList.length > 0 && leaflet.id
    )

    if (leafletsWithFiles.length === 0) {
      return []
    }

    processingStage.value = 'uploading'
    try {
      const attempts: UploadAttempt[] = await Promise.all(
        leafletsWithFiles.map(async leaflet => {
          try {
            const files = leaflet.fileList.filter(f => f.raw).map(f => f.raw as File)
            const formData = new FormData()
            formData.append('uniqueId', leaflet.naturalId)
            files.forEach(file => {
              formData.append('files', file, file.name)
            })

            const initialStatus = await uploadLeafletFilesAsync(formData, leaflet.id!, leaflet.watermark)
            return { leaflet, initialStatus }
          } catch (err) {
            const error = err as { response?: { data?: unknown } }
            return {
              leaflet,
              error: checkKnownMessages(error.response?.data)
            }
          }
        })
      )

      processingStage.value = 'generating'
      const successfulAttempts = attempts.filter(
        (attempt): attempt is UploadAttempt & { initialStatus: LeafletProcessingStatusApiModel } =>
          attempt.initialStatus != null
      )
      const statuses = await waitForProcessingBatch(successfulAttempts.map(attempt => attempt.initialStatus))

      return attempts.map(attempt => {
        if (attempt.error) {
          return {
            id: attempt.leaflet.id,
            naturalId: attempt.leaflet.naturalId,
            requestFailed: true,
            message: attempt.error
          }
        }

        const status = attempt.initialStatus ? statuses.get(attempt.initialStatus.leafletId) : undefined
        if (status?.status === 'READY') {
          return {
            id: attempt.leaflet.id,
            naturalId: attempt.leaflet.naturalId,
            requestFailed: false
          }
        }

        return {
          id: attempt.leaflet.id,
          naturalId: attempt.leaflet.naturalId,
          requestFailed: true,
          message: status?.error || i18n.global.t('leaflets.leafletsCreate.generationFailed')
        }
      })
    } finally {
      processingStage.value = 'idle'
    }
  }

  async function waitForProcessingBatch(
    initialStatuses: LeafletProcessingStatusApiModel[]
  ): Promise<Map<string, LeafletProcessingStatusApiModel>> {
    const statuses = new Map(initialStatuses.map(status => [status.leafletId, status]))
    const pending = new Set(
      initialStatuses
        .filter(status => status.status !== 'READY' && status.status !== 'FAILED')
        .map(status => status.leafletId)
    )

    if (pending.size === 0) {
      return statuses
    }

    const startedAt = Date.now()
    try {
      while (pending.size > 0 && Date.now() - startedAt < processingTimeoutMs) {
        await delay(processingPollIntervalMs)
        const polledStatuses = await fetchLeafletsProcessingStatus([...pending])
        const returnedIds = new Set<string>()

        polledStatuses.forEach(status => {
          statuses.set(status.leafletId, status)
          returnedIds.add(status.leafletId)
          if (status.status === 'READY' || status.status === 'FAILED') {
            pending.delete(status.leafletId)
          }
        })

        for (const leafletId of [...pending]) {
          if (!returnedIds.has(leafletId)) {
            statuses.set(leafletId, {
              leafletId,
              jobId: statuses.get(leafletId)?.jobId ?? null,
              status: 'FAILED',
              error: i18n.global.t('leaflets.leafletsCreate.generationFailed')
            })
            pending.delete(leafletId)
          }
        }
      }
    } catch (err) {
      const error = err as { response?: { data?: unknown } }
      const message = checkKnownMessages(error.response?.data)
      for (const leafletId of pending) {
        statuses.set(leafletId, {
          leafletId,
          jobId: statuses.get(leafletId)?.jobId ?? null,
          status: 'FAILED',
          error: message
        })
      }
      pending.clear()
    }

    for (const leafletId of pending) {
      statuses.set(leafletId, {
        leafletId,
        jobId: statuses.get(leafletId)?.jobId ?? null,
        status: 'FAILED',
        error: i18n.global.t('leaflets.leafletsCreate.generationTimeout')
      })
    }

    return statuses
  }

  async function uploadCoversForLeaflets(
    createdLeaflets: Array<{
      id?: string
      naturalId: string
      fileList?: FileItem[]
      firstPageFile: FileItem[]
      watermark?: boolean
      requestFailed?: boolean
    }>
  ): Promise<Array<{ id?: string; naturalId: string; requestFailed: boolean; message?: string }>> {
    const leafletsWithCovers = createdLeaflets.filter(
      leaflet => !leaflet.requestFailed && leaflet.firstPageFile.length > 0 && leaflet.id
    )

    const uploadPromises = leafletsWithCovers.map(async leaflet => {
      try {
        const firstPageFile = leaflet.firstPageFile[0]
        if (!firstPageFile.raw) {
          return {
            id: leaflet.id,
            naturalId: leaflet.naturalId,
            requestFailed: false
          }
        }

        const formData = new FormData()
        formData.append('firstPage', firstPageFile.raw)

        await uploadLeafletCover(formData, leaflet.id!)

        return {
          id: leaflet.id,
          naturalId: leaflet.naturalId,
          requestFailed: false
        }
      } catch (err) {
        const error = err as { response?: { data?: unknown } }
        return {
          id: leaflet.id,
          naturalId: leaflet.naturalId,
          requestFailed: true,
          message: checkKnownMessages(error.response?.data)
        }
      }
    })

    return Promise.all(uploadPromises)
  }

  function createScheduledLongLinkPayload(
    scheduledLink: ScheduledLongLink,
    leafletId: string
  ): ScheduledLongLinkEventPayload | null {
    if (!scheduledLink.date) {
      return null
    }

    const plannedTime = new Date(`${scheduledLink.date} ${scheduledLink.time}`)

    return {
      contextId: leafletId,
      plannedTime: plannedTime.toISOString(),
      rootContextId: leafletId,
      context: 'LEAFLET',
      type: 'UPDATE_LEAFLET_LONG_URL',
      longUrls: scheduledLink.longUrls,
      devices: scheduledLink.devices
    }
  }

  function createScheduledThumbnailLinkPayload(
    scheduledLink: ScheduledThumbnailLink,
    leafletId: string
  ): ScheduledThumbnailLinkEventPayload | null {
    if (!scheduledLink.date) {
      return null
    }

    const plannedTime = new Date(`${scheduledLink.date} ${scheduledLink.time}`)

    return {
      contextId: leafletId,
      plannedTime: plannedTime.toISOString(),
      rootContextId: leafletId,
      context: 'LEAFLET',
      type: 'UPDATE_LEAFLET_THUMBNAIL_URL',
      thumbnailURL: scheduledLink.thumbnailURL,
      devices: scheduledLink.devices
    }
  }

  async function sendScheduledEventsForLeaflets(
    tabs: LeafletTabData[],
    createdLeaflets: Array<{ id: string; naturalId: string }>
  ): Promise<boolean> {
    const scheduledEvents: ScheduledEventPayload[] = []

    tabs.forEach((tab, index) => {
      const leaflet = createdLeaflets[index]
      if (!leaflet || !leaflet.id) {
        return
      }

      const longLinkPayload = createScheduledLongLinkPayload(tab.scheduledLongLink, leaflet.id)
      if (longLinkPayload) {
        scheduledEvents.push(longLinkPayload)
      }

      const thumbnailPayload = createScheduledThumbnailLinkPayload(tab.scheduledThumbnailTrackingLink, leaflet.id)
      if (thumbnailPayload) {
        scheduledEvents.push(thumbnailPayload)
      }
    })

    if (scheduledEvents.length === 0) {
      return false
    }

    try {
      const { status } = await scheduleEventsBatch(scheduledEvents)
      return status === 200
    } catch {
      return false
    }
  }

  function getFailedLeaflets(
    createdLeaflets: Array<{ naturalId: string; requestFailed?: boolean; message?: string }>,
    fileUploadResults: Array<{ naturalId: string; requestFailed?: boolean; message?: string }>,
    coverUploadResults: Array<{ naturalId: string; requestFailed?: boolean; message?: string }>
  ): Array<{ naturalId: string; message: string }> {
    const allResults = [...createdLeaflets, ...fileUploadResults, ...coverUploadResults]

    return allResults
      .filter(result => result.requestFailed)
      .map(result => ({
        naturalId: result.naturalId,
        message: result.message || 'Unknown error'
      }))
  }

  return {
    prepareLeafletPayload,
    handleGroupCreation,
    createLeaflets,
    uploadFilesForLeaflets,
    uploadCoversForLeaflets,
    sendScheduledEventsForLeaflets,
    getFailedLeaflets,
    processingStage
  }
}

export type UseLeafletCreateFlowReturn = ReturnType<typeof useLeafletCreateFlow>
